/**
 * Pure-function UI copy policy for call reconnection banners.
 *
 * Web port of `kaether_for_ios/Stoat/E2EE/CallReconnectionUIPolicy.swift`.
 *
 * Given a `CallReconnectionState`, returns the banner text and display
 * attributes the UI should render. This module is deliberately side-effect
 * free — no DOM, no React/SolidJS, no navigator APIs.
 *
 * ## Path-aware copy
 * The `unstable` state carries a `networkPath` hint supplied by the consumer.
 * When the path is `"cellular"` the copy calls out the mobile data connection
 * specifically (mirrors iOS which uses "mobile data"); on web `"wifi"` covers
 * both Wi-Fi and wired Ethernet.
 *
 * The consumer is responsible for reading `navigator.connection.type` and
 * mapping it to `NetworkPath` before entering the `unstable` state.
 */

import type { CallReconnectionState } from "./CallReconnectionState";

// ---------------------------------------------------------------------------
// Return type
// ---------------------------------------------------------------------------

/**
 * Visual severity of the banner. Maps to a colour in the design system:
 * - `"none"` — no banner shown (connected / connecting state).
 * - `"warning"` — amber; degraded but recoverable.
 * - `"error"` — red; terminal failure.
 * - `"neutral"` — grey; informational (e.g. ended).
 */
export type BannerSeverity = "none" | "warning" | "error" | "neutral";

/** The complete UI policy result for one state. */
export interface ReconnectionBannerPolicy {
  /**
   * Whether to show a banner at all. False for `connected` and the brief
   * `connecting` state (the call's own loading indicator handles that).
   */
  readonly showBanner: boolean;
  /** Primary short text displayed in the banner. */
  readonly title: string;
  /** Optional secondary text with more detail / user action hint. */
  readonly subtitle: string | null;
  /** Visual severity driving colour / icon selection. */
  readonly severity: BannerSeverity;
}

// ---------------------------------------------------------------------------
// Pure policy function
// ---------------------------------------------------------------------------

/**
 * Return the banner policy for the given state.
 *
 * @param state - Current `CallReconnectionState`.
 * @returns `ReconnectionBannerPolicy` — fully describes what the UI should show.
 */
export function reconnectionBannerPolicy(
  state: CallReconnectionState,
): ReconnectionBannerPolicy {
  switch (state.kind) {
    case "connecting":
      return {
        showBanner: false,
        title: "Connecting…",
        subtitle: null,
        severity: "none",
      };

    case "connected":
      return {
        showBanner: false,
        title: "",
        subtitle: null,
        severity: "none",
      };

    case "unstable": {
      // Path-aware copy: cellular vs Wi-Fi vs unknown.
      const pathLabel = pathAwareUnstableTitle(state.networkPath);
      return {
        showBanner: true,
        title: pathLabel.title,
        subtitle: pathLabel.subtitle,
        severity: "warning",
      };
    }

    case "reconnecting":
      return {
        showBanner: true,
        title: "Reconnecting…",
        subtitle: "Trying to restore your encrypted call.",
        severity: "warning",
      };

    case "disconnectedAfterFailure":
      return {
        showBanner: true,
        title: "Call disconnected",
        subtitle: "The connection could not be restored. Please try again.",
        severity: "error",
      };

    case "ended":
      return {
        showBanner: true,
        title: "Call ended",
        subtitle: null,
        severity: "neutral",
      };
  }
}

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

interface PathCopy {
  title: string;
  subtitle: string;
}

/**
 * Returns path-aware copy for the `unstable` state.
 *
 * Mirrors iOS `CallReconnectionUIPolicy.unstableTitle(path:)`.
 * - cellular → calls out mobile data explicitly
 * - wifi / unknown → generic Wi-Fi / connection wording
 */
function pathAwareUnstableTitle(
  path: "cellular" | "wifi" | "unknown",
): PathCopy {
  switch (path) {
    case "cellular":
      return {
        title: "Poor mobile data connection",
        subtitle:
          "Your mobile data signal is weak. Move to a better area or switch to Wi-Fi.",
      };
    case "wifi":
      return {
        title: "Poor Wi-Fi connection",
        subtitle:
          "Your Wi-Fi signal is weak. Move closer to the router or switch to mobile data.",
      };
    case "unknown":
      return {
        title: "Poor connection",
        subtitle: "Your connection is unstable. Trying to maintain the call.",
      };
  }
}
